"use client";

import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { NextLink, type NextLinkProps } from "@components/ui/next-link/NextLink";
import { isAllowedExternalUrl } from "@contentful-api/helpers/isAllowedExternalUrl";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useAuthFormStore } from "@store/authFormStore";
import type { ContentLink as ContentLinkModel } from "@lib/contentful/mappers/mapContentLink";
import type { LinkModel } from "@lib/contentful/mappers/parts/link/types";
import { Text, type TextProps } from "@ecco/ui/text";
import { defaultUnderlineStyle } from "@ecco/ui/css/common";
import { Button, type ButtonProps } from "@ecco/ui/button";
import { Link, type LinkProps } from "@ecco/ui/link";
import type { MouseEvent, ReactNode } from "react";

// Modals that let the user go back after following a link to another modal.
const MODALS_WITH_BACK_NAVIGATION = new Set<string>([
    ModalTypes.createAccount,
    ModalTypes.login,
    ModalTypes.upgradeAccount,
]);

// The old ModalLink look: bold text that turns gray on hover.
const modalLinkStyles = {
    transitionProperty: "common",
    transitionDuration: "fast",
    transitionTimingFunction: "ease-out",
    ...defaultUnderlineStyle,
    fontWeight: "bold",
    textDecoration: "none",
    _hover: { color: "gray.500", textDecorationColor: "gray.500" },
    "& span": { fontWeight: "bold" },
    _focusVisible: {
        outline: "2px solid",
        outlineColor: "gray.500",
        outlineOffset: "2px",
        textDecoration: "none",
    },
};

// Keeps the deep link target clear of the sticky header.
const HEADER_OFFSET = 120;

const scrollToDeepLinkTarget = (event: MouseEvent<HTMLElement>, targetId: string): void => {
    event.preventDefault();
    const element = document.getElementById(targetId);
    if (!element) return;

    const scroll = () =>
        window.scrollTo({
            top: element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
            behavior: "smooth",
        });

    // A target inside a closed accordion item opens it first and scrolls once the panel is open.
    const accordionButton = element
        .closest(".chakra-accordion__item")
        ?.querySelector<HTMLButtonElement>("button");
    if (!accordionButton) return scroll();

    if (accordionButton.getAttribute("aria-expanded") === "false") accordionButton.click();
    setTimeout(scroll, 100);
};

// Modules map links with mapLink, layouts still with mapContentLink. Until the layouts move to
// mapLink, a module link is rendered through the layout shape.
const toContentLinkModel = (link: LinkModel): ContentLinkModel => {
    if (link.type === "modal") return { ...link, kind: "modal" };

    const { href, label } = link;
    const openInNewTab = link.openInNewTab ?? false;

    if (href.startsWith("#")) return { kind: "deepLink", targetId: href.slice(1), label };
    if (href.startsWith("/")) return { kind: "internal", href, label, openInNewTab };

    return { kind: "external", href, label, openInNewTab };
};

type ManagedProps = "as" | "children" | "href" | "onClick" | "rel" | "target";

type ContentLinkProps = Omit<LinkProps, ManagedProps> & {
    link: ContentLinkModel | LinkModel;
    children?: ReactNode;
    // Runs before a module link opens its modal or scrolls to its deep link target.
    onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export const ContentLink = ({ link: linkModel, children, ...props }: ContentLinkProps) => {
    const { showModal, modalType: openModalType } = useGlobalModalContext();
    const { get: getMicrocopy } = useMicrocopy();
    const setReturnModal = useAuthFormStore((state) => state.setReturnModal);
    // Module links keep what they did on develop through the old Link: the modal label, back
    // navigation, the modal test id and the deep link scroll.
    const isModuleLink = "type" in linkModel;
    const link = isModuleLink ? toContentLinkModel(linkModel) : linkModel;
    const content = children ?? link.label;

    switch (link.kind) {
        case "internal":
            return (
                <NextLink
                    {...(props as NextLinkProps)}
                    href={link.href}
                    target={link.openInNewTab ? "_blank" : "_self"}
                >
                    {content}
                </NextLink>
            );
        case "external":
            if (!isAllowedExternalUrl(link.href)) return null;

            return (
                <Link
                    {...props}
                    href={link.href}
                    target={link.openInNewTab ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                >
                    {content}
                </Link>
            );
        case "deepLink":
            return (
                <NextLink
                    {...(props as NextLinkProps)}
                    href={`#${link.targetId}`}
                    {...(isModuleLink && {
                        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                            props.onClick?.(event);
                            scrollToDeepLinkTarget(event, link.targetId);
                        },
                    })}
                >
                    {content}
                </NextLink>
            );
        case "modal": {
            const openModal = () =>
                showModal(link.modalType, {
                    description: link.description,
                    descriptionRichText: link.descriptionRichText,
                    ...(isModuleLink && { label: link.label }),
                    target: link.target,
                    ...(link.modalType === ModalTypes.giftWrapping && {
                        microcopies: {
                            title:
                                getMicrocopy(CommonCMS.page, CommonCMS.giftWrapping.modalTitle) ??
                                "",
                        },
                    }),
                });

            if (!isModuleLink) {
                return (
                    <Button {...(props as unknown as ButtonProps)} onClick={openModal}>
                        {content}
                    </Button>
                );
            }

            // A text button, as the old ModalLink rendered it, so a wrapping Button keeps its own look.
            return (
                <Text
                    as="button"
                    // Typeless, it would be a form's default button: Enter in a field opens the modal.
                    type="button"
                    data-type="modal-link"
                    data-testid={`${link.modalType}-link`}
                    color="inherit"
                    fontSize="inherit"
                    cursor="pointer"
                    {...(props as unknown as TextProps)}
                    onClick={(event: MouseEvent<HTMLElement>) => {
                        props.onClick?.(event);
                        event.preventDefault();
                        if (openModalType && MODALS_WITH_BACK_NAVIGATION.has(openModalType)) {
                            setReturnModal(openModalType);
                        }
                        openModal();
                    }}
                    sx={{ ...props.sx, ...modalLinkStyles }}
                >
                    {content}
                </Text>
            );
        }
    }
};
